import React, { useState } from "react";
import {
  Divider,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { IoIosArrowUp } from "react-icons/io";
import InputField from "./InputField";
import InputSelecter from "./InputSelecter";
import PhoneNumberField from "./PhoneNumberField";
import { GrGallery } from "react-icons/gr";
import { CiEdit } from "react-icons/ci";
import SearchableSelect from "./SearchableSelect"; // Import the SearchableSelect component

const PersonalDetailsForm = ({
  formData,
  handleFormChange,
  images,
  setImages,
  dateOfBirth,
  handleDateChange,
  height,
  handleHeightChange,
  highschoolList,
  handleHighschoolChange,

  educationList,
  handleEducationChange,
  handleAddEducation,
  handleRemoveEducation,

  occupationList,
  handleOccupationChange,
  handleAddOccupation,
  handleRemoveOccupation,

  awardsList,
  handleAwardsChange,
  handleAddAwards,
  handleRemoveAwards,

  rewardsList,
  handleRewardsChange,
  handleAddRewards,
  handleRemoveRewards,

  medalsList,
  handleMedalsChange,
  handleAddMedals,
  handleRemoveMedals,

  certificationsList,
  handleCertificationsChange,
  handleAddCertifications,
  handleRemoveCertifications,
  handlePhoneNumberChange,
  handleCodeChange,
  handleCountryChange,
  castData,
  religionData,
  dietData,
  maritalData
}) => {
  // State to track open/close of the form
  const [isOpen, setIsOpen] = useState(true);

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  // Handle cast selection change
  const handleCastChange = (event) => {
    handleFormChange(event);
  };

  // Month options for dropdown
  const monthOptions = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Convert castData to options format for SearchableSelect
  const castOptions = castData?.map((item) => ({
    label: item.text,
    value: item.text
  })) || [];

  return (
    <div className="w-full border-gray-300 my-4">
      {/* Header Row with Arrow */}
      <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={toggleOpen}>
        <h2 className="text-xl font-semibold mb-0 relative">
          Personal Details
        </h2>

        {/* Horizontal Divider with Dots */}
        <div className="relative flex-1 mx-6 h-4">
          <div className="absolute top-1/2 w-full border-t border-gray-300 -translate-y-1/2" />
          <span className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 h-2 w-2 bg-gray-300 rounded-full" />
          <span className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 h-2 w-2 bg-gray-300 rounded-full" />
        </div>

        {/* Arrow Icon changes based on isOpen */}
        <IoIosArrowUp
          size={24}
          className={`
           transition-transform duration-300
           ${isOpen ? "rotate-0" : "rotate-180"}`}
        />
      </div>

      {/* Collapsible Container */}
      <div
        className={`
          overflow-hidden 
          transition-all 
          duration-300
          ${isOpen ? "max-h-max" : "max-h-0"} 
        `}
      >

        {/* Image Upload Section */}
        <label className="block font-medium mb-2">Images</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:flex gap-3 mb-4">
          {images.map((img, index) => (
            <label key={index} className="cursor-pointer relative group">
              <div className="w-full md:w-24 aspect-square border border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-100 relative">
                {img ? (
                  <img
                    src={typeof img === "string" ? img : URL.createObjectURL(img)}
                    alt="Uploaded"
                    className="w-full h-full object-cover rounded-xl"
                  />
                ) : (
                  <span className="text-gray-400">
                    <GrGallery size={24} />
                  </span>
                )}
                {/* Overlay div */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-colors duration-200 rounded-lg">
                  <CiEdit className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={24} />
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const newImages = [...images];
                    newImages[index] = file;
                    setImages(newImages);
                  }
                }}
              />
            </label>
          ))}
        </div>
        {/* #1 Basic Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <InputField
            label="First Name"
            name="firstname"
            placeholder="Enter First Name"
            value={formData.firstname}
            onChange={handleFormChange}
          />
          <InputField
            label="Last Name"
            name="lastname"
            placeholder="Enter Last Name"
            value={formData.lastname}
            onChange={handleFormChange}
          />
          <InputField
            type="email"
            label="Email ID"
            name="email"
            placeholder="Enter Email ID"
            value={formData.email}
            readOnly={true}
          />
        </div>

        {/* #2 Date of Birth - Updated with Month Selector */}
        <label className="block mb-2">Date Of Birth</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <InputField
            type="number"
            label="Day"
            name="day"
            placeholder="DD"
            value={dateOfBirth.day}
            onChange={handleDateChange}
            maxLength={2}
          />
          
          {/* Month Selector using InputSelecter */}
          <InputSelecter
            label="Month"
            name="month"
            value={dateOfBirth.month || ""}
            onChange={handleDateChange}
            options={monthOptions}
          />
          
          <InputField
            type="number"
            label="Year"
            name="year"
            placeholder="YYYY"
            value={dateOfBirth.year}
            onChange={handleDateChange}
            maxLength={4}
          />
        </div>

        {/* #3 Gender, Religion, Cast */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <div>
            <FormLabel>Gender</FormLabel><span className="text-[14px] text-[#E72848]">*</span>
            <RadioGroup
              row
              name="gender"
              value={formData.gender}
              onChange={handleFormChange}
            >
              <FormControlLabel
                value="Male"
                control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />}
                label="Male"
              />
              <FormControlLabel
                value="Female"
                control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />}
                label="Female"
              />
            </RadioGroup>
          </div>
          <InputSelecter
            label="Your Religion"
            name="religion"
            value={formData.religion}
            onChange={handleFormChange}
            options={religionData && religionData.map((item) => ({
              label: item.text,
              value: item.text
            }))}
          />
          {/* Updated Cast Field with SearchableSelect */}
          <SearchableSelect
            label="Your Cast"
            name="cast"
            value={formData.cast}
            onChange={handleCastChange}
            options={castOptions}
            placeholder="Search cast..."
          />
        </div>

        {/* #4 Height, Weight, Phone */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <div className="grid grid-cols-2 gap-2">
            <InputField
              label="Height (ft)"
              name="ft"
              placeholder="e.g. 5"
              value={height.ft}
              onChange={handleHeightChange}
            />
            <InputField
              label="Height (in)"
              name="in"
              placeholder="e.g. 2"
              value={height.in}
              onChange={handleHeightChange}
            />
          </div>
          <InputField
            label="Weight"
            name="weight"
            placeholder="Enter Your Weight"
            value={formData.weight}
            onChange={handleFormChange}
          />
          <div>
            <h2 className="mb-1">Phone Number<span className="text-[14px] text-[#E72848]">*</span></h2>
            <PhoneNumberField
              value={formData.phone_number}
              setPhoneNumber={(newPhone) =>
                handlePhoneNumberChange("phone_number", newPhone)
              }
              disabled={false}
              code={formData.country_code}
              setCode={(newCode) =>
                handleCodeChange("country_code", newCode)
              }
            />
          </div>
        </div>

        {/* #5 Diet, Native Place */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <InputSelecter
            label="Your Diet"
            name="diet"
            value={formData.diet}
            onChange={handleFormChange}
            options={dietData && dietData.map((item) => ({
              label: item.text,
              value: item.text
            }))}
          />
          <InputField
            label="City"
            name="city"
            placeholder="city"
            value={formData.city || ""}
            onChange={handleFormChange}
          />
          <InputField
            label="Country"
            name="country"
            placeholder="country"
            value={formData.country || ""}
            onChange={handleFormChange}
          />

          <div className="md:col-span-2 hidden">
            <InputField
              label="Native Place"
              placeholder="Your Native Place Address"
            />
          </div>
        </div>

        {/* #6 Visa, Years in Country, Nationality */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <InputField
            label="Visa Status"
            name="visa_status"
            placeholder="Enter Visa Status"
            value={formData.visa_status || ""}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="# Of Years In Country"
            name="years_in_country"
            placeholder="Enter # of Years"
            value={formData.years_in_country}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="Nationality"
            name="nationality"
            placeholder="Enter Nationality"
            value={formData.nationality}
            onChange={handleFormChange}
          />
        </div>

        {/* #7 Marital Status, Live With Family, Instagram */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <InputSelecter
            label="Marital Status"
            name="marital_status"
            value={formData.marital_status}
            onChange={handleFormChange}
            options={maritalData && maritalData.map((item) => ({
              label: item.text,
              value: item.text
            }))}
          />
          <div>
            <label className="block text-base font-medium text-gray-700 my-1">
              You Live With Your Family?<span className="text-[14px] text-[#E72848]">*</span>
            </label>
            <RadioGroup
              row
              name="live_with_family"
              value={formData.live_with_family ? "true" : "false"}
              onChange={handleFormChange}
            >
              <FormControlLabel value="true" control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />} label="Yes" />
              <FormControlLabel value="false" control={<Radio sx={{ '&.Mui-checked': { color: '#E72848' } }} />} label="No" />
            </RadioGroup>
          </div>
          <InputField
            label="Instagram Username"
            name="instagram"
            placeholder="@username"
            value={formData.instagram}
            onChange={handleFormChange}
            required={false}
          />
        </div>

        {/* #8 Socials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <InputField
            label="LinkedIn Username"
            name="linkedin"
            placeholder="@username"
            value={formData.linkedin}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="Snapchat Username"
            name="snapchat"
            placeholder="@username"
            value={formData.snapchat}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="Twitter Username"
            name="twitter"
            placeholder="@username"
            value={formData.twitter}
            onChange={handleFormChange}
            required={false}
          />
        </div>

        {/* #9 Hobbies, Extra Skills */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6 px-1">
          <div className="md:col-span-1">
            <InputField
              label="Facebok Username"
              name="facebook"
              placeholder="@username"
              value={formData.facebook}
              onChange={handleFormChange}
              required={false}
            />
          </div>

          <div className="md:col-span-1">
            <InputField
              label="Hobbies"
              name="hobbies"
              placeholder="Your hobbies"
              value={formData.hobbies}
              onChange={handleFormChange}
            />
          </div>
          <div className="md:col-span-1">
            <InputField
              label="Extra Skills"
              name="extra_skills"
              placeholder="Your skills"
              value={formData.extra_skills}
              onChange={handleFormChange}
            />
          </div>
        </div>

        {/* Dynamic Education Section - College Name Field Removed */}
        <Divider sx={{ my: 2 }} />
        <h3 className="text-lg font-semibold mb-2">Education</h3>
        {educationList && educationList?.map((education, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4 px-1">
            <div className="md:col-span-2">
              <InputField
                label="Degree"
                name="degree"
                placeholder="e.g. Bachelor of Engineering"
                value={education.degree || ""}
                onChange={(e) => handleEducationChange(index, e)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="Year From"
                name="from_year"
                placeholder="e.g. 2014"
                value={education.from_year || ""}
                onChange={(e) => handleEducationChange(index, e)}
                maxLength={4}
                required={false}
              />
              <InputField
                label="Year To"
                name="to_year"
                placeholder="e.g. 2018"
                value={education.to_year || ""}
                onChange={(e) => handleEducationChange(index, e)}
                maxLength={4}
                required={false}
              />
            </div>
            <div className="md:col-span-2">
              <InputField
                label="University Name"
                name="university_name"
                placeholder="e.g. Mumbai University"
                value={education.university_name || ""}
                onChange={(e) => handleEducationChange(index, e)}
                required={false}
              />
            </div>
            <div className="md:col-span-1">
              <InputField
                label="City"
                name="city"
                placeholder="e.g. Mumbai"
                value={education.city || ""}
                onChange={(e) => handleEducationChange(index, e)}
                required={false}
              />
            </div>

            <div className="col-span-full text-center">
              {educationList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveEducation(index)}
                  className="max-w-max text-[#E72848] underline"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="text-center mb-2">
          <button
            type="button"
            onClick={handleAddEducation}
            className="max-w-max text-[#0A79DF] underline px-3 py-1 rounded m-auto"
          >
            Add More Education
          </button>
        </div>

        {(highschoolList && highschoolList.length > 0 ? highschoolList : [{}]).map((highschool, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4 px-1 border-b pb-4">
            <div className="md:col-span-2">
              <InputField
                label="School Name"
                name="school_name"
                placeholder="Enter School name"
                value={highschool.school_name || ""}
                onChange={(e) => handleHighschoolChange(index, e)}
                required={false}
              />
            </div>
            <div className="md:col-span-1">
              <InputField
                label="City"
                name="city"
                placeholder="Enter City name"
                value={highschool.city || ""}
                onChange={(e) => handleHighschoolChange(index, e)}
                required={false}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="Year From"
                name="from_year"
                placeholder="0000"
                value={highschool.from_year || ""}
                onChange={(e) => handleHighschoolChange(index, e)}
                maxLength={4}
                required={false}
              />
              <InputField
                label="Year To"
                name="to_year"
                placeholder="0000"
                value={highschool.to_year || ""}
                onChange={(e) => handleHighschoolChange(index, e)}
                maxLength={4}
                required={false}
              />
            </div>
          </div>
        ))}

        {/* Rewards */}
        <h3 className="text-lg font-semibold mb-2">Rewards</h3>

        {(rewardsList && rewardsList.length > 0 ? rewardsList : [{}]).map((reward, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2 px-1">
            <div className="md:col-span-2">
              <InputField
                label="Reward Title"
                name="title"
                placeholder="e.g. Employee of the Year"
                value={reward.title || ""}
                onChange={(e) => handleRewardsChange(index, e)}
                required={false}
              />
            </div>
            <div>
              <InputField
                label="Year"
                name="year"
                placeholder="e.g. 2022"
                value={reward.year || ""}
                onChange={(e) => handleRewardsChange(index, e)}
                maxLength={4}
                required={false}
              />
            </div>

            <div className="col-span-full text-center">
              {rewardsList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveRewards(index)}
                  className="max-w-max text-[#E72848] underline"
                >
                  Remove Reward
                </button>
              )}
            </div>
          </div>
        ))}

        <div className="text-center mb-2">
          <button
            type="button"
            onClick={handleAddRewards}
            className="max-w-max text-[#0A79DF] underline px-3 py-1 rounded m-auto"
          >
            Add More Rewards
          </button>
        </div>

        {/* Awards */}
        <h3 className="text-lg font-semibold mb-2">Awards</h3>

        {(awardsList && awardsList.length > 0 ? awardsList : [{}]).map((award, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2 px-1">
            <div className="md:col-span-2">
              <InputField
                label="Award Title"
                name="title"
                placeholder="e.g. Best Performer Award"
                value={award.title || ""}
                onChange={(e) => handleAwardsChange(index, e)}
                required={false}
              />
            </div>
            <div>
              <InputField
                label="Year"
                name="year"
                placeholder="e.g. 2021"
                value={award.year || ""}
                onChange={(e) => handleAwardsChange(index, e)}
                maxLength={4}
                required={false}
              />
            </div>

            <div className="col-span-full text-center">
              {awardsList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveAwards(index)}
                  className="max-w-max text-[#E72848] underline"
                >
                  Remove Award
                </button>
              )}
            </div>
          </div>
        ))}

        <div className="text-center mb-2">
          <button
            type="button"
            onClick={handleAddAwards}
            className="max-w-max text-[#0A79DF] underline px-3 py-1 rounded m-auto"
          >
            Add More Awards
          </button>
        </div>

        {/* Medals */}
        <h3 className="text-lg font-semibold mb-2">Medals</h3>

        {(medalsList && medalsList.length > 0 ? medalsList : [{}]).map((medal, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2 px-1">
            <div className="md:col-span-2">
              <InputField
                label="Medal Title"
                name="title"
                placeholder="e.g. Gold Medal"
                value={medal.title || ""}
                onChange={(e) => handleMedalsChange(index, e)}
                required={false}
              />
            </div>
            <div>
              <InputField
                label="Year"
                name="year"
                placeholder="e.g. 2020"
                value={medal.year || ""}
                onChange={(e) => handleMedalsChange(index, e)}
                maxLength={4}
                required={false}
              />
            </div>

            <div className="col-span-full text-center">
              {medalsList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveMedals(index)}
                  className="max-w-max text-[#E72848] underline"
                >
                  Remove Medal
                </button>
              )}
            </div>
          </div>
        ))}

        <div className="text-center mb-2">
          <button
            type="button"
            onClick={handleAddMedals}
            className="max-w-max text-[#0A79DF] underline px-3 py-1 rounded m-auto"
          >
            Add More Medals
          </button>
        </div>

        {/* Certifications */}
        <h3 className="text-lg font-semibold mb-2">Certifications</h3>

        {(certificationsList && certificationsList.length > 0 ? certificationsList : [{}]).map((certification, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2 px-1">
            <div className="md:col-span-2">
              <InputField
                label="Certification Title"
                name="title"
                placeholder="e.g. AWS Certified Developer"
                value={certification.title}
                onChange={(e) => handleCertificationsChange(index, e)}
                required={false}
              />
            </div>
            <div>
              <InputField
                label="Year"
                name="year"
                placeholder="e.g. 2023"
                value={certification.year}
                onChange={(e) => handleCertificationsChange(index, e)}
                maxLength={4}
                required={false}
              />
            </div>

            <div className="col-span-full text-center">
              {certificationsList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveCertifications(index)}
                  className="max-w-max text-[#E72848] underline"
                >
                  Remove Certification
                </button>
              )}
            </div>
          </div>
        ))}

        <div className="text-center mb-2">
          <button
            type="button"
            onClick={handleAddCertifications}
            className="max-w-max text-[#0A79DF] underline px-3 py-1 rounded m-auto"
          >
            Add More Certifications
          </button>
        </div>

        {/* Dynamic Occupation Section */}
        <Divider sx={{ my: 2 }} />
        <h3 className="text-lg font-semibold mb-2">Occupation</h3>
        {occupationList && occupationList?.map((occupation, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4 px-1">
            <div className="md:col-span-2">
              <InputField
                label="Role"
                name="role"
                placeholder="e.g. Software Engineer"
                value={occupation.role || ""}
                onChange={(e) => handleOccupationChange(index, e)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="Year From"
                name="from_year"
                placeholder="e.g. 2020"
                value={occupation.from_year || ""}
                onChange={(e) => handleOccupationChange(index, e)}
                maxLength={4}
                required={false}
              />
              <InputField
                label="Year To"
                name="to_year"
                placeholder="e.g. 2022"
                value={occupation.to_year || ""}
                onChange={(e) => handleOccupationChange(index, e)}
                maxLength={4}
                required={false}
              />
            </div>
            <div className="col-span-full">
              <InputField
                label="Company Name"
                name="company_name"
                placeholder="e.g. Tech Corp"
                value={occupation.company_name}
                onChange={(e) => handleOccupationChange(index, e)}
                required={false}
              />
            </div>
            <div className="col-span-full text-center">
              {occupationList.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveOccupation(index)}
                  className="max-w-max text-[#E72848] underline"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        <div className="text-center">
          <button
            type="button"
            onClick={handleAddOccupation}
            className="max-w-max text-[#0A79DF] underline px-3 py-1 rounded m-auto"
          >
            Add More Occupation
          </button>
        </div>
      </div>
    </div >
  );
};

export default PersonalDetailsForm;